import type { Key } from 'react';
import type { SortField } from '@/types';

/**
 * Antd `order` at a known column key to domain `SortField` (or `null` when cleared). Called from
 * `Table` `onChange`; keeps the sort state in the domain model so `sortData`, `onSort`, and
 * analytics don't need to know about antd's `ascend`/`descend`.
 */
export const tableOrderToSortField = (
  columnKey: Key | readonly Key[] | undefined,
  order: 'ascend' | 'descend' | null | undefined
): SortField | null => {
  if (columnKey === undefined || Array.isArray(columnKey)) return null;
  const key = String(columnKey);
  if (order === 'ascend') return { key, direction: 'asc' };
  if (order === 'descend') return { key, direction: 'desc' };
  return null;
};

/**
 * Per-column `sortOrder` for controlled antd columns, derived from the single active `SortField`.
 * Returns `undefined` for non-active columns so antd leaves their indicator unset.
 * Accepts `readonly Key[]` (e.g. array `dataIndex`) and treats it as no-match, consistent with
 * `tableOrderToSortField`.
 */
export const sortOrderForColumn = (
  columnKey: Key | readonly Key[] | undefined,
  sortField: SortField | null
): 'ascend' | 'descend' | undefined => {
  if (!sortField || columnKey === undefined || Array.isArray(columnKey)) {
    return undefined;
  }
  if (String(columnKey) !== sortField.key) return undefined;
  return sortField.direction === 'asc' ? 'ascend' : 'descend';
};
